import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseService';
import { Search, Plus, Trash2, Clock, X, Edit, Loader2 } from 'lucide-react';
import { BullhornNoteEditor } from './BullhornNoteEditor';

interface VacancyNote {
    id: string;
    vacancy_id: string;
    author_name: string;
    content: string;
    created_at: string;
    updated_at: string;
}

interface VacancyNotesProps {
    vacancyId: string;
    currentUser: any;
}

export const VacancyNotes: React.FC<VacancyNotesProps> = ({ vacancyId, currentUser }) => {
    const [notes, setNotes] = useState<VacancyNote[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Editor State
    const [isEditing, setIsEditing] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [noteContent, setNoteContent] = useState('');

    useEffect(() => {
        if (!vacancyId) return;
        fetchNotes();
    }, [vacancyId]);

    const fetchNotes = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from('vacancy_notes')
                .select('*')
                .eq('vacancy_id', vacancyId)
                .order('created_at', { ascending: false });
                
            if (error) {
                console.warn(error);
                setNotes([]);
            } else if (data) {
                setNotes(data);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm("Weet je zeker dat je deze notitie wilt verwijderen?")) return;
        try {
            const { error } = await supabase.from('vacancy_notes').delete().eq('id', id);
            if (error) throw error;
            setNotes(notes.filter(n => n.id !== id));
        } catch (e) {
            console.error(e);
            alert("Kon notitie niet verwijderen.");
        }
    };

    const openEditor = (note?: VacancyNote) => {
        if (note) {
            setEditingNoteId(note.id);
            setNoteContent(note.content);
        } else {
            setEditingNoteId(null);
            setNoteContent('');
        }
        setIsEditing(true);
    };

    const closeEditor = () => {
        setIsEditing(false);
        setEditingNoteId(null);
        setNoteContent('');
    };

    const safeFormatDate = (dateString: string) => {
        try { return new Date(dateString).toLocaleString('nl-NL'); }
        catch { return String(dateString); }
    };

    const filteredNotes = notes.filter(n => n.content.toLowerCase().includes(searchTerm.toLowerCase()) || n.author_name.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div className="w-full flex flex-col h-full bg-white relative">
            <div className="px-6 py-4 flex flex-col md:flex-row md:items-center justify-between border-b border-slate-200 gap-4">
                <h2 className="text-[16px] font-black text-slate-800 flex items-center gap-2">
                    Notities <span className="bg-slate-100 text-slate-500 text-[10px] px-2 py-0.5 rounded-full">{notes.length}</span>
                </h2>
                
                {!isEditing && (
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                            <input
                                type="text"
                                placeholder="Zoek notities..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[12px] font-medium text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all"
                            />
                        </div>
                        <button 
                            onClick={() => openEditor()} 
                            className="flex items-center gap-2 bg-[#e51b23] text-white px-4 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-widest hover:bg-[#B71C1C] transition-colors"
                        >
                            <Plus size={14} /> Nieuwe Notitie
                        </button>
                    </div>
                )}
            </div>

            {isEditing ? (
                <div className="flex-1 w-full bg-white relative animate-fadeIn overflow-y-auto">
                    <BullhornNoteEditor
                        mode="vacancy"
                        targetId={vacancyId}
                        currentUser={currentUser}
                        onClose={closeEditor}
                        onSaved={() => { fetchNotes(); closeEditor(); }}
                        initialData={editingNoteId ? {
                            id: editingNoteId,
                            content: noteContent
                        } : undefined}
                    />
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto p-0">
                    {isLoading ? (
                        <div className="flex justify-center p-12"><Loader2 className="animate-spin text-slate-300" size={32} /></div>
                    ) : filteredNotes.length === 0 ? (
                        <div className="p-12 text-center flex flex-col items-center justify-center">
                            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-slate-300 mb-4"><Edit size={24} /></div>
                            <h3 className="text-sm font-bold text-slate-900 mb-1">Geen Notities Gevonden</h3>
                            <p className="text-xs text-slate-500 font-medium mb-6">Voeg notities toe om belangrijke informatie over deze vacature bij te houden.</p>
                            <button onClick={() => openEditor()} className="flex items-center gap-2 bg-white border border-[#e51b23] text-[#e51b23] px-5 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-red-50 transition-colors">Start met typen</button>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-slate-50/80 sticky top-0 z-10 backdrop-blur-sm border-b border-slate-200">
                                <tr>
                                    <th className="py-3 px-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Datum</th>
                                    <th className="py-3 px-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Auteur</th>
                                    <th className="py-3 px-6 text-[10px] font-black uppercase tracking-widest text-slate-400 w-1/2">Notitie</th>
                                    <th className="py-3 px-6 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Acties</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredNotes.map(note => (
                                    <tr key={note.id} className="hover:bg-slate-50/50 transition-colors group">
                                        <td className="py-4 px-6 align-top">
                                            <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500">
                                                <Clock size={12} className="text-slate-300"/> {safeFormatDate(note.created_at)}
                                            </div>
                                        </td>
                                        <td className="py-4 px-6 align-top">
                                            <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                                                <div className="w-5 h-5 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-[9px] uppercase">{note.author_name.charAt(0)}</div>
                                                {note.author_name}
                                            </div>
                                        </td>
                                        <td className="py-4 px-6 align-top">
                                            <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap">{note.content}</p>
                                        </td>
                                        <td className="py-4 px-6 align-top text-right">
                                            <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button onClick={() => openEditor(note)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Bewerken"><Edit size={14}/></button>
                                                <button onClick={() => handleDelete(note.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" title="Verwijderen"><Trash2 size={14}/></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
};
